import { guardEmptyString } from '@portal/utils/util-guards';
import { componentFactoryOf } from 'vue-tsx-support';

import { ImageDecoding, ImageLoading } from '@jtnews/shared/images';

import { injectStylesMixin } from '../../../mixins/inject-styles';
import { JtnUiAvatar } from '../../jtn-ui-avatar';
import { JtnUiImage } from '../../jtn-ui-image';
import { JtnUiTypography } from '../../jtn-ui-typography';

import styles from './jtn-ui-record-authors.styles.scss?module';

interface Events {
  onClickedAuthor: {
    event: Event;
    url: string;
    id: number;
    name: string;
  };
}

type Author = {
  id: string;
  url?: string;
  name: string;
  targetUrl?: string;
  jobPosition: string;
  image?: {
    fileName: string;
    width: number;
    height: number;
    img: ImageVM;
    sources: ImageSourceVM[];
  };
};

type ImageSourceVM = {
  srcset: string;
  sizes?: string | undefined;
  type?: string | undefined;
  media?: string | undefined;
};

type ImageVM = {
  src: string;
  srcset?: string | undefined;
  sizes?: string | undefined;
  loading?: ImageLoading | undefined;
  decoding?: ImageDecoding | undefined;
  author?: string | undefined;
  description?: string | undefined;
};

const DEFAULT_ONE_AUTHOR_AVATAR_SIZE = 48;
const DEFAULT_AUTHORS_AVATAR_SIZE = 32;

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'JtnUiRecordAuthors',
    props: {
      authors: {
        type: Array as () => Author[],
        default() {
          return [];
        }
      }
    },
    methods: {
      getComponent(author: Author) {
        if (author.url) {
          return {
            attrs: {
              tag: 'a',
              href: author.url,
              target: author.targetUrl,
              itemprop: 'url'
            }
          };
        }

        return {
          attrs: {
            tag: 'span'
          }
        };
      }
    },
    render() {
      const attributes = {
        attrs: this.$attrs,
        on: this.$listeners
      };

      return (
        <section
          class={[styles.recordAuthors, this.authors.length === 1 ? styles.one : '']}
          {...attributes}
        >
          <div class={styles.authorsContainer}>
            {this.authors.map((author: Author, index: number) => (
              <div
                key={index}
                class={styles.author}
                itemprop="author"
                itemscope
                itemtype="http://schema.org/Person"
              >
                <JtnUiTypography
                  {...this.getComponent(author)}
                  type="font-vijet-type-1"
                  nativeOnClick={(event: Event) => {
                    this.$emit('clickedAuthor', {
                      event,
                      url: author.url,
                      id: author.id,
                      name: author.name
                    });
                  }}
                >
                  <JtnUiAvatar
                    type="author"
                    size={
                      this.authors.length > 1
                        ? DEFAULT_AUTHORS_AVATAR_SIZE
                        : DEFAULT_ONE_AUTHOR_AVATAR_SIZE
                    }
                  >
                    {guardEmptyString(author.image?.img.src as string) && (
                      <JtnUiImage
                        slot="photo"
                        src={author.image?.img.src}
                        sources={author?.image?.sources}
                        width={author?.image?.width}
                        height={author?.image?.height}
                        loading={author?.image?.img.loading}
                        decoding={author?.image?.img.decoding}
                        alt="Фото автора"
                      />
                    )}
                  </JtnUiAvatar>
                  <div class={styles.authorDescription}>
                    <p class={styles.authorName} itemprop="name">
                      {author.name}
                    </p>
                    <p class={styles.authorPosition} itemprop="position">
                      {author.jobPosition}
                    </p>
                  </div>
                </JtnUiTypography>
              </div>
            ))}
          </div>
        </section>
      );
    }
  });
